/**
 * RBAC-4.2 · Copy one person's access onto another (FR-31, NFR-5).
 *
 * Pure, no DOM, no Supabase. The colleagues, the difference and the copy come from
 * `20261102a1_rbac_4_2_copy_person_access.sql` via `services/personAccessCopyService.ts`. The screen
 * is `components/settings/components/users/CopyAccessDialog.tsx`, opened from `PersonRoleCard`.
 *
 * ⚠️ ACCESS IS ROLE-SHAPED. No table holds a per-person permission, so a copy gives the target the
 * colleague's role, through RBAC-4.1's `assign_person_role()` and therefore through the same
 * triggers as Change role. The database decides every refusal; `copyAccessView().refusal` only
 * says so before the admin confirms.
 *
 * ⚠️ A COPY NEVER HANDS ON A RESERVED PERMISSION (AC 3), even when the admin holds it. The role
 * carrying it cannot be split, so it is not copied at all, and the sentence says to use Change role.
 *
 * ⚠️ ONE ROLE OR NONE (Allan, 2026-11-02). A colleague with two roles in this account is not copied;
 * moving a one-role person onto the second role system as a side effect is what RBAC-4.1 declined.
 */

import { PERMISSION_CONSEQUENCES, consequenceFor } from './permissionConsequences';
import { rendersInEditor } from './permissionDisplay';
import { cannotChangeSentence, type PersonRoleAssignment } from './personRole';
import type { RpcErrorLike } from './roleDuplication';
import { listOf, permissionTitle, type DiffLine } from './rolePermissionDiff';

export const PERSON_ACCESS_COPY_SOURCES_RPC = 'person_access_copy_sources';
export const PERSON_ACCESS_COPY_PREVIEW_RPC = 'person_access_copy_preview';
export const COPY_PERSON_ACCESS_RPC = 'copy_person_access';

export interface CopySource {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  roleName: string | null;
}

export type CopyStatus =
  | 'copyable'
  | 'reserved'
  | 'privileged'
  | 'multiple'
  | 'no_role'
  | 'same'
  | 'target_self'
  | 'target_privileged';

const STATUSES: readonly CopyStatus[] = ['copyable', 'reserved', 'privileged', 'multiple', 'no_role', 'same', 'target_self', 'target_privileged'];

export interface CopyRole {
  roleId: number;
  name: string;
}

export interface CopyPreview {
  targetUserId: string;
  targetPrimaryRoleId: number | null;
  sourceUserId: string;
  sourceFirstName: string;
  sourceLastName: string;
  sourceIsActive: boolean;
  /** The colleague's `users.role_id`; the copy is binding on it. */
  sourcePrimaryRoleId: number | null;
  /** The colleague's own roles in this account. */
  sourceRoles: CopyRole[];
  /** Roles the colleague holds through a parent account or in another account. Never copied. */
  sourceRolesElsewhere: number;
  /** The one role a copy would give. Null when there is none, or more than one. */
  role: CopyRole | null;
  status: CopyStatus;
  /** Reserved permission keys the role carries. */
  reserved: string[];
  gaining: string[];
  losing: string[];
}

const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};
const str = (v: unknown) => (typeof v === 'string' ? v : '');
const isNames = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');

const role = (v: unknown): CopyRole | null => {
  if (!v || typeof v !== 'object') return null;
  const r = v as Record<string, unknown>;
  const id = num(r.role_id);
  return id === null ? null : { roleId: id, name: str(r.name) };
};

export function parseCopySources(raw: unknown): CopySource[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object' && typeof (r as Record<string, unknown>).user_id === 'string')
    .map((r) => ({
      userId: r.user_id as string,
      firstName: str(r.first_name),
      lastName: str(r.last_name),
      email: str(r.email),
      isActive: r.is_active !== false,
      roleName: typeof r.role_name === 'string' ? r.role_name : null,
    }));
}

/** The RPC's jsonb → view model. An unknown status is null, never a guess. */
export function parseCopyPreview(raw: unknown): CopyPreview | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.target_user_id !== 'string' || typeof r.source_user_id !== 'string') return null;
  if (!STATUSES.includes(r.status as CopyStatus)) return null;
  if (!isNames(r.reserved) || !isNames(r.gaining) || !isNames(r.losing)) return null;
  return {
    targetUserId: r.target_user_id,
    targetPrimaryRoleId: num(r.target_primary_role_id),
    sourceUserId: r.source_user_id,
    sourceFirstName: str(r.source_first_name),
    sourceLastName: str(r.source_last_name),
    sourceIsActive: r.source_is_active !== false,
    sourcePrimaryRoleId: num(r.source_primary_role_id),
    sourceRoles: (Array.isArray(r.source_roles) ? r.source_roles : []).map(role).filter((x): x is CopyRole => x !== null),
    sourceRolesElsewhere: num(r.source_roles_elsewhere) ?? 0,
    role: role(r.role),
    status: r.status as CopyStatus,
    reserved: r.reserved,
    gaining: r.gaining,
    losing: r.losing,
  };
}

const fullName = (s: Pick<CopySource, 'firstName' | 'lastName'>) => `${s.firstName} ${s.lastName}`.trim();

export function sourceOptionLabel(s: CopySource): string {
  const parts = [fullName(s) || s.email || 'Unnamed person', s.roleName ?? 'No role'];
  if (!s.isActive) parts.push('deactivated');
  return parts.join(' · ');
}

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Every word of the query, in any order, against name, email and role. */
export function filterCopySources(sources: readonly CopySource[], query: string): CopySource[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...sources];
  return sources.filter((s) => {
    const hay = fold(`${fullName(s)} ${s.email} ${s.roleName ?? ''}`);
    return words.every((w) => hay.includes(w));
  });
}

export interface CopyAccessView {
  title: string;
  /** Who the copy is for, as the headings name them: the first name, or the full name for two Sarahs. */
  targetName: string;
  /** What the copy does, in one or two sentences. Null when it cannot be applied. */
  summary: string | null;
  gaining: DiffLine[];
  losing: DiffLine[];
  /** Said instead of an empty diff when the copy changes the role but nothing the screen lists. */
  noDifference: string | null;
  /** Hidden and reader-pending permissions in the difference, counted, never listed by key. */
  unlisted: string | null;
  /** Non-null when the database would refuse: the confirm is disabled and this is shown. */
  refusal: string | null;
  notes: string[];
  canApply: boolean;
  confirmLabel: string;
}

/**
 * One person, named: "Noah will be able to …". The catalogue's sentences are written for a role's
 * holders ("3 people will …", "Nobody on this role …"), so the count form is used only as the
 * fallback for a key the catalogue has no sentence for.
 */
const lines = (keys: readonly string[], direction: 'gained' | 'lost', who: string): DiffLine[] =>
  keys
    .filter(rendersInEditor)
    .sort((a, b) => permissionTitle(a).localeCompare(permissionTitle(b)))
    .map((k) => {
      const c = PERMISSION_CONSEQUENCES[k];
      const sentence = c ? `${who} will ${direction === 'gained' ? c.gained : c.lost}.` : consequenceFor(k, direction, 1);
      return { key: k, title: permissionTitle(k), sentence };
    });

/** AC 1 and AC 3: the difference and every refusal, in words, before anything is applied. */
export function copyAccessView(input: { preview: CopyPreview; target: PersonRoleAssignment }): CopyAccessView {
  const { preview: p, target } = input;
  // Two Sarahs: "Give Sarah the same access as Sarah?" says nothing, so full names for both.
  const clash = target.firstName.trim() !== '' && fold(target.firstName.trim()) === fold(p.sourceFirstName.trim());
  const targetName = (clash ? `${target.firstName} ${target.lastName}` : target.firstName).trim();
  const them = targetName || 'this person';
  const Them = targetName || 'This person';
  const src = (clash ? `${p.sourceFirstName} ${p.sourceLastName}` : p.sourceFirstName).trim();
  const Src = src || 'This person';
  const roleName = p.role?.name ?? '';

  const notes: string[] = [];
  if (p.sourceRolesElsewhere === 1) notes.push(`${Src} also holds 1 role through another account. That is not copied.`);
  if (p.sourceRolesElsewhere > 1) notes.push(`${Src} also holds ${p.sourceRolesElsewhere} roles through other accounts. Those are not copied.`);
  if (!p.sourceIsActive) notes.push(`${Src} is deactivated. Their role can still be copied.`);

  let refusal: string | null = null;
  switch (p.status) {
    case 'reserved':
      refusal =
        // Quoted: the labels themselves contain "and" ("Change account settings and administer people").
        `${Src} is on ${roleName}, which carries ${listOf(p.reserved.map((k) => `\u201c${permissionTitle(k)}\u201d`))}. ` +
        `A copy never hands those on, so nothing is copied. To give ${them} ${roleName}, use Change role, where that is a decision of its own.`;
      break;
    case 'privileged':
      refusal = `${Src} is on ${roleName}. Only a Joules superadmin can give it, so it cannot be copied.`;
      break;
    case 'multiple': {
      const names = p.sourceRoles.map((r) => r.name).sort((a, b) => a.localeCompare(b));
      refusal =
        `${Src} holds ${names.length} roles in this account: ${listOf(names)}. ` +
        `A copy gives one role, so ${src ? `${src}'s` : 'their'} access cannot be copied as one. Use Change role to choose the one ${them} should hold.`;
      break;
    }
    case 'no_role':
      refusal = `${Src} has no role to copy.`;
      break;
    case 'same':
      refusal = `${Them} already has the same role as ${src || 'this person'}: ${roleName}. Nothing would change.`;
      break;
    case 'target_self':
      refusal = cannotChangeSentence({ canChange: false, cannotChangeReason: 'self' });
      break;
    case 'target_privileged':
      refusal = cannotChangeSentence({ canChange: false, cannotChangeReason: 'privileged' });
      break;
    case 'copyable':
      break;
  }

  const canApply = p.status === 'copyable' && p.role !== null;
  const replaced = target.roles.filter((r) => r.origin === 'primary' || r.origin === 'account').map((r) => r.name);
  const summary = canApply
    ? `${Src} is on ${roleName}. ${Them} will be on ${roleName} too${replaced.length ? `, replacing ${listOf(replaced)}` : ''}.`
    : null;

  const gaining = canApply ? lines(p.gaining, 'gained', Them) : [];
  const losing = canApply ? lines(p.losing, 'lost', Them) : [];
  const hidden = canApply ? p.gaining.length + p.losing.length - gaining.length - losing.length : 0;

  return {
    title: `Give ${Them} the same access as ${src || 'this person'}?`,
    targetName: Them,
    summary,
    gaining,
    losing,
    noDifference: canApply && gaining.length === 0 && losing.length === 0 ? "Nothing this screen shows changes. Only the role's name does." : null,
    unlisted: hidden <= 0 ? null : hidden === 1 ? 'Plus 1 more permission this screen does not list.' : `Plus ${hidden} more permissions this screen does not list.`,
    refusal,
    notes,
    canApply,
    confirmLabel: src ? `Copy ${src}'s access` : 'Copy access',
  };
}

/** Binding on both: the role the dialog showed each of them holding. Either moved is 22023. */
export function copyPersonAccessArgs(target: Pick<PersonRoleAssignment, 'userId' | 'primaryRoleId'>, p: Pick<CopyPreview, 'sourceUserId' | 'sourcePrimaryRoleId'>) {
  return {
    p_target_user_id: target.userId,
    p_source_user_id: p.sourceUserId,
    p_expected_target_role_id: target.primaryRoleId,
    p_expected_source_role_id: p.sourcePrimaryRoleId,
  };
}

export function isStaleCopy(err: RpcErrorLike | null | undefined): boolean {
  return err?.code === '22023' && /changed since you opened/.test(err.message ?? '');
}
